import { matchesProperty } from 'lodash-es';
import { ExtendableError } from '../../../utils/errors';

export class FieldNotFoundError extends ExtendableError {}

// A helper type to enforce that objects have a `name` property.
export type TWithName = { name: string };

export function findByName<T extends TWithName>(array: T[], name: string): T | undefined {
    return array.find(matchesProperty('name', name));
}

export class FieldsArray<T extends TWithName> extends Array<T> {
    // This override is just for TypeScript compliance, it is not needed for runtime JS.
    override slice(start?: number, end?: number): this {
        return super.slice(start, end) as this;
    }

    // "Smart" narrowing, only if T['name'] is a literal union (like TranslatedFormField
    // not just 'string').
    getByName<TName extends T extends { name: infer U } ? (string extends U ? never : U) : never>(
        name: TName
    ): Extract<T, { name: TName }>;

    // General case: T['name'] is string, so just return T.
    getByName(name: string): T;

    // Runtime implementation.
    getByName(name: string) {
        const found = findByName(this, name);

        if (!found) {
            throw new FieldNotFoundError(`No field found named: '${name}'.`);
        }

        return found;
    }

    removeByName(name: string): void {
        const index = this.indexOf(this.getByName(name));
        if (index > -1) {
            this.splice(index, 1);
        }
    }

    replaceByName(name: string, field: T): void {
        const index = this.indexOf(this.getByName(name));
        if (index > -1) {
            this.splice(index, 1, field);
        }
    }

    insertAfter(name: string, field: T): void {
        const index = this.indexOf(this.getByName(name));
        if (index > -1) {
            this.splice(index + 1, 0, field);
        }
    }
}
